import { MainContainer } from "@/components/mainContainer";
import { EventHeader } from "@/components/eventHeader";
import { FilterBar } from "@/components/FilterBar";
import {
  Box,
  Button,
  Divider,
  FormControl,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import InputMask from "react-input-mask";
import { MenuActionButton } from "@/components/menuActionButton";
import * as Fonts from "../../../components/texts";
import * as T from "../../../components/table";
import * as Inputs from "../../../components/inputs";
import { MenuPopUp } from "@/components/menuPopUp";
import { Pagination } from "@/components/pagination";
import { CheckIcon, SearchIcon } from "@/components/icons";
import { validateCpf } from "@/utils/validatorUtil";
import Joi from "joi";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { useCustomToast } from "@/hooks/useToast";
import { useEventClient } from "@/hooks/useEventClient";
import {
  EventByIdDto,
  EventClientCreateDto,
  EventClientIndexDto,
  EventClientIndexFilterDto,
  EventClientUpdateDto,
} from "../types";
import { useParams } from "react-router-dom";
import { useEvent } from "@/hooks/useEvent";
import LoadingOverlay from "@/components/overlay";

const initialFilter: EventClientIndexFilterDto = {
  name: "",
  cpf: "",
  email: "",
};

const schema = Joi.object({
  name: Joi.string().required().messages({
    "string.empty": "Nome deve ser informado",
  }),
  email: Joi.string().required().messages({
    "string.empty": "E-mail deve ser informado",
  }),
  cpf: Joi.string()
    .required()
    .pattern(/[0-9]/)
    .messages({
      "string.empty": "CPF deve ser informado",
      "string.invalid": "CPF inválido",
    })
    .custom((value, helper) => {
      const valueReplaced = value
        .replaceAll(".", "")
        .replaceAll("-", "")
        .replaceAll("_", "");

      const isValidCpf = validateCpf(valueReplaced);

      if (!isValidCpf || valueReplaced.length !== 11) {
        return helper.error("string.invalid");
      }

      return value;
    }),
  entrance: Joi.array().items(
    Joi.object({
      quantityInvitation: Joi.number().required().min(0).messages({
        "number.base": "Quantidade de convites deve ser informada",
        "any.required": "Quantidade de convites deve ser informada",
        "number.min": "Quantidade de convites deve ser maior que zero",
      }),
    })
  ),
});

export const EventClients = () => {
  const { customToast } = useCustomToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [showMenuPopUp, setShowMenuPopUp] = useState<undefined | number>();
  const [page, setPage] = useState<number>(1);
  const perPage = 50;
  const {
    getEventClients,
    eventClientCreate,
    eventClientUpdate,
    eventClientDelete,
    eventClientImport,
  } = useEventClient();
  const { getEventById } = useEvent();
  const [event, setEvent] = useState<EventByIdDto | null>(null);
  const [lastPage, setLastPage] = useState<number>(1);
  const [filter, setFilter] =
    useState<EventClientIndexFilterDto>(initialFilter);
  const [cpfError, setCpfError] = useState<boolean | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [clients, setClients] = useState<EventClientIndexDto[]>([]);
  const [totalClients, setTotalClients] = useState<number>(0);
  const { eventId } = useParams<{ eventId?: string }>();
  const parsedEventId = Number(eventId);
  const [editingClient, setEditingClient] =
    useState<EventClientUpdateDto | null>(null);
  const isEditing = !!editingClient;
  const [filterType, setFilterType] = useState("");

  const onImportFile = (file: any) => {
    setIsLoading(true);

    eventClientImport(Number(eventId), file)
      .then(() => {
        customToast("success", "Importado com sucesso");
      })
      .catch((error: any) =>
        customToast("error", [error.response.data.message].join(", "))
      )
      .finally(() => {
        callbackEventClient();
        setIsLoading(false);
      });
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<EventClientCreateDto>({
    resolver: joiResolver(schema, {
      abortEarly: false,
      allowUnknown: true,
    }),
  });

  const cpf = watch("cpf");

  const onChangeCpfValue = (event: ChangeEvent<HTMLInputElement>) => {
    clearErrors("cpf");
    setCpfError(false);

    const value = event.target.value
      .replaceAll(".", "")
      .replaceAll("-", "")
      .replaceAll("_", "");

    const isValidNumberValue = /^[0-9]*$/.test(value);
    const validatedValue = isValidNumberValue ? value : cpf;

    setValue("cpf", validatedValue);
  };

  const onBlurCpfValue = () => {
    if (!cpf) return;

    const value = cpf
      .replaceAll(".", "")
      .replaceAll("-", "")
      .replaceAll("_", "");
    const isValidCpf = validateCpf(value);

    if (!isValidCpf) {
      setCpfError(true);
    }
  };

  const cpfInputColor = useMemo(() => {
    if (!cpf && !cpfError) return "#EAEAEA";
    if (errors?.cpf?.message || cpfError) return "red";

    const cpfWithoutMask = cpf
      .replaceAll(".", "")
      .replaceAll("-", "")
      .replaceAll("_", "");

    if (cpfWithoutMask.length === 11) return "#62CD0F";

    return "#EAEAEA";
  }, [cpf, errors?.cpf?.message, cpfError]);

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen]);

  const onSubmit = (data: EventClientCreateDto) => {
    setIsLoading(true);

    if (!event?.id) {
      customToast("error", "Evento não encontrado.");
      setIsLoading(false);
      return;
    }

    const payload: EventClientCreateDto = {
      ...data,
      eventId: event.id,
      entrance: data.entrance.map((e: any) => ({
        eventEntranceId: Number(e.eventEntranceId),
        quantityInvitation: Number(e.quantityInvitation),
      })),
    };

    let request;

    if (isEditing && editingClient?.id) {
      request = eventClientUpdate(editingClient.id, payload);
    } else {
      request = eventClientCreate(payload);
    }

    request
      .then((response: any) => {
        const client: EventClientIndexDto = response.data;
        setClients((prev) =>
          isEditing
            ? prev.map((c) => (c.id === client.id ? client : c))
            : [...prev, client]
        );
        customToast(
          "success",
          isEditing
            ? "Cliente atualizado com sucesso"
            : "Cliente cadastrado com sucesso"
        );
        reset();
        onClose();
      })
      .catch((error: any) => {
        customToast(
          "error",
          isEditing
            ? error.response?.data?.message
            : error.response?.data?.message
        );
      })
      .finally(() => {
        callbackEventClient();
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (editingClient && event?.entrance) {
      const mergedEntrances = event.entrance.map((eventEntrance) => {
        const matched = editingClient.entrance?.find(
          (e: any) => Number(e.id) === Number(eventEntrance.id)
        );

        return {
          eventEntranceId: eventEntrance.id,
          name: eventEntrance.type,
          quantityInvitation: matched?.quantityInvitation,
        };
      });

      reset({
        name: editingClient.name,
        email: editingClient.email,
        cpf: editingClient.cpf,
        entrance: mergedEntrances,
      });
    } else if (event?.entrance) {
      const defaultEntrances = event.entrance.map((e) => ({
        eventEntranceId: e.id,
        name: e.type,
        quantityInvitation: undefined,
        quantity: undefined,
      }));

      reset({
        name: "",
        email: "",
        cpf: "",
        entrance: defaultEntrances,
      });
      setEditingClient(null);
    }
  }, [editingClient, event?.entrance, reset]);

  const handleEditClient = (client: EventClientUpdateDto) => {
    setEditingClient(client);
    onOpen();
  };

  const handleDelete = async (id: number) => {
    eventClientDelete(id)
      .then(() => {
        setClients((prev) => prev.filter((item) => item.id !== id));
        customToast("success", "Cliente removido com sucesso");
      })
      .catch((error: any) => {
        customToast("error", error.response.data.message);
      });
  };

  const callbackEventClient = useCallback(() => {
    setIsLoading(true);
    getEventClients({ page, perPage, eventId: parsedEventId, ...filter })
      .then((response) => {
        setClients(response.data.data);
        setTotalClients(response.data.total);
        setLastPage(response.data.lastPage);
      })
      .catch((error: any) =>
        customToast("error", [error.response.data.message].join(", "))
      )
      .finally(() => setIsLoading(false));
  }, [page, filter]);

  useEffect(() => {
    if (!eventId) return;
    const getEvent = async () => {
      try {
        const response = await getEventById(parsedEventId);
        setEvent(response.data);
      } catch (error: any) {
        customToast("error", error.response?.data?.message);
      }
    };

    getEvent();
  }, [eventId]);

  useEffect(
    () => callbackEventClient(),
    [page, filter.name, filter.email, filter.cpf]
  );

  const entranceHeaders = useMemo(() => {
    return event?.entrance ?? [];
  }, [event?.entrance]);

  const handleCancelModal = () => {
    onClose();
    setEditingClient(null);
    reset();
  };

  const handleNewClient = () => {
    setEditingClient(null);
    onOpen();
    reset({
      name: "",
      email: "",
      cpf: "",
      entrance:
        event?.entrance?.map((e) => ({
          eventEntranceId: e.id,
          name: e.type,
          quantityInvitation: undefined,
          quantity: undefined,
        })) || [],
    });
  };

  const filters = [
    {
      field: (
        <HStack w="30%" justifyContent="flex-end" key={1}>
          <Text
            fontFamily="Inter"
            fontStyle="normal"
            fontWeight="400"
            fontSize="14px"
            lineHeight="17px"
            color="neutral.500"
          >
            Buscar por:
          </Text>
          <Select
            w="70%"
            h={9}
            borderRadius={4}
            bg="neutral.0"
            onChange={(e) => {
              const selected = e.target.value;
              setFilterType(selected);
              setFilter({});
            }}
          >
            <option value="">Selecione</option>
            <option value="name">Nome do Cliente</option>
            <option value="email">E-mail</option>
            <option value="cpf">CPF</option>
          </Select>
        </HStack>
      ),
    },
    {
      field: (
        <InputGroup alignItems="center" key={2}>
          <InputLeftElement pointerEvents="none" top="" left={1}>
            <SearchIcon />
          </InputLeftElement>
          <Inputs.SimpleText
            placeholder="O que você procura?"
            w="100%"
            h={9}
            borderRadius={4}
            textIndent="35px"
            bg="neutral.0"
            onChange={(e) => {
              const value = e.target.value;
              setFilter({ ...filter, [filterType]: value });
            }}
          />
        </InputGroup>
      ),
    },
  ];

  return (
    <>
      <EventHeader eventName={event?.name} />
      <MainContainer
        height={isLoading || clients.length < 5 ? undefined : "100%"}
      >
        {isLoading && <LoadingOverlay />}
        <FilterBar
          title={`Clientes Cadastrados (${totalClients})`}
          subTitle="Lista de clientes do evento"
          buttons={[
            {
              title: "Adicionar cliente",
              onClick: handleNewClient,
            },
            {
              title: "Importar planilha",
              onClick: onImportFile,
              type: "inputFile",
            },
          ]}
          filters={filters}
        />
        <Modal
          isOpen={isOpen}
          onClose={() => {
            onClose();
            setEditingClient(null);
          }}
          isCentered
          closeOnEsc={false}
          closeOnOverlayClick={false}
        >
          <ModalOverlay />
          <ModalContent
            left={142}
            bgColor="neutral.100"
            as="form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <ModalHeader>
              {isEditing ? "Editar Cliente" : "Cadastro de Cliente"}
            </ModalHeader>
            <ModalBody>
              <Stack spacing={5}>
                <FormControl w="100%">
                  <Fonts.FormLabelText>Nome do Cliente</Fonts.FormLabelText>
                  <Input
                    type="text"
                    textIndent={"1px"}
                    w="100%"
                    h={10}
                    borderRadius={3}
                    border="1px solid #EAEAEA"
                    bgColor="neutral.0"
                    {...register("name")}
                  />
                  {errors?.name?.message && (
                    <Text
                      fontFamily="Lato"
                      fontStyle="normal"
                      fontWeight="500"
                      fontSize="11px"
                      lineHeight="18px"
                      color="fail.500"
                    >
                      {errors.name.message as string}
                    </Text>
                  )}
                </FormControl>
                <FormControl w="100%">
                  <Fonts.FormLabelText>E-mail</Fonts.FormLabelText>
                  <Input
                    type="email"
                    textIndent={"1px"}
                    w="100%"
                    h={10}
                    borderRadius={3}
                    border="1px solid #EAEAEA"
                    bgColor="neutral.0"
                    {...register("email")}
                  />
                  {errors?.email?.message && (
                    <Text
                      fontFamily="Lato"
                      fontStyle="normal"
                      fontWeight="500"
                      fontSize="11px"
                      lineHeight="18px"
                      color="fail.500"
                    >
                      {errors.email.message as string}
                    </Text>
                  )}
                </FormControl>
                <FormControl w="100%">
                  <Fonts.FormLabelText>CPF</Fonts.FormLabelText>
                  <InputGroup>
                    <Input
                      {...register("cpf")}
                      as={InputMask as React.ElementType}
                      mask="***.***.***-**"
                      type="text"
                      textIndent={"1px"}
                      w="100%"
                      h={10}
                      borderRadius={3}
                      border="1px solid #EAEAEA"
                      bgColor="neutral.0"
                      borderColor={cpfInputColor}
                      onChange={onChangeCpfValue}
                      onBlur={onBlurCpfValue}
                    />
                    {cpf &&
                      validateCpf(
                        cpf
                          .replaceAll(".", "")
                          .replaceAll("-", "")
                          .replaceAll("_", "")
                      ) && (
                        <InputRightElement>
                          <CheckIcon color="green.500" />
                        </InputRightElement>
                      )}
                  </InputGroup>
                  {errors?.cpf?.message && (
                    <Text
                      fontFamily="Lato"
                      fontStyle="normal"
                      fontWeight="500"
                      fontSize="11px"
                      lineHeight="18px"
                      color="fail.500"
                    >
                      {errors.cpf.message as string}
                    </Text>
                  )}
                </FormControl>

                {event?.entrance?.map((values, index) => {
                  return (
                    <FormControl w="100%" key={values.id} mb={4}>
                      <Fonts.FormLabelText>
                        {`${values.type} — Quantidade de convites`}
                      </Fonts.FormLabelText>
                      <input
                        type="hidden"
                        {...register(`entrance.${index}.eventEntranceId`)}
                        value={values.id}
                      />
                      <Input
                        type="number"
                        placeholder="Ex: 100, 200, 300..."
                        textIndent="1px"
                        w="100%"
                        h={10}
                        borderRadius={3}
                        border="1px solid #EAEAEA"
                        bgColor="neutral.0"
                        min={0}
                        {...register(`entrance.${index}.quantityInvitation`)}
                      />
                      {(errors?.entrance as any)?.[index]?.quantityInvitation
                        ?.message && (
                        <Text
                          fontFamily="Lato"
                          fontStyle="normal"
                          fontWeight="500"
                          fontSize="11px"
                          lineHeight="18px"
                          color="fail.500"
                        >
                          {
                            (errors.entrance as any)?.[index]
                              ?.quantityInvitation?.message
                          }
                        </Text>
                      )}
                    </FormControl>
                  );
                })}
              </Stack>
            </ModalBody>
            <ModalFooter>
              <HStack w="100%" spacing={7}>
                <Button
                  variant="outline"
                  borderColor="secondary.500"
                  colorScheme="secondary.500"
                  w="100%"
                  h={9}
                  borderRadius={4}
                  onClick={handleCancelModal}
                  _hover={{
                    bgColor: "secondary.100",
                  }}
                  _active={{
                    bgColor: "secondary.300",
                  }}
                >
                  <Fonts.ButtonText color="secondary.500">
                    Cancelar
                  </Fonts.ButtonText>
                </Button>
                <Button
                  variant="solid"
                  type="submit"
                  bgColor="secondary.500"
                  w="100%"
                  h={9}
                  borderRadius={4}
                  _hover={{
                    bgColor: "secondary.300",
                  }}
                  _active={{
                    bgColor: "secondary.500",
                  }}
                >
                  <Fonts.ButtonText>Confirmar</Fonts.ButtonText>
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Stack p={5} mt="0 !important" w="100%">
          <Box bgColor="neutral.100" borderRadius={5} p={3}>
            <HStack w="100%" spacing={5} mt={5}>
              <Stack w="75%">
                <Text
                  fontFamily="Inter"
                  fontStyle="normal"
                  fontWeight="600"
                  fontSize="24px"
                  lineHeight="31px"
                  color="neutral.600"
                >
                  Informações do Evento
                </Text>
              </Stack>
            </HStack>
            <Divider h={2} />
            <Stack p={2}>
              <Stack key={event?.id}>
                <HStack w="100%" spacing={5} mt={5}>
                  <HStack w="40%">
                    <Text fontWeight="semibold">Evento:</Text>
                    <Text>{event?.name}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">Tipo do Evento:</Text>
                    <Text>{event?.type?.name}</Text>
                  </HStack>
                </HStack>
                <HStack w="100%" spacing={5} mt={5}>
                  <HStack w="40%">
                    <Text fontWeight="semibold">Unidade:</Text>
                    <Text>{event?.branch?.name}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">Data e Hora:</Text>
                    <Text>{new Date(event?.date || "").toLocaleString()}</Text>
                  </HStack>
                </HStack>
                <HStack w="100%" spacing={5} mt={5}>
                  <HStack w="40%">
                    <Text fontWeight="semibold">Tipo do Pacote:</Text>
                    <Text>{event?.team?.name}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="semibold">Local:</Text>
                    <Text>{event?.local}</Text>
                  </HStack>
                </HStack>
              </Stack>
            </Stack>
          </Box>

          <Stack w="100%">
            <TableContainer w="100%" mt={5}>
              <Table>
                <Thead bgColor="neutral.300">
                  <Tr>
                    <Th w="35%">
                      <Fonts.TableTheadText>
                        Nome do Cliente
                      </Fonts.TableTheadText>
                    </Th>
                    <Th textAlign="center">
                      <Fonts.TableTheadText>E-mail</Fonts.TableTheadText>
                    </Th>
                    <Th textAlign="center">
                      <Fonts.TableTheadText>CPF</Fonts.TableTheadText>
                    </Th>
                    {entranceHeaders.map((entrance) => {
                      const hasAny = clients.some((c) =>
                        c.entrance?.some(
                          (e) =>
                            e.id === entrance.id &&
                            (e.quantityInvitation ?? 0) > 0
                        )
                      );
                      return (
                        <Th key={entrance?.id} textAlign="center">
                          <Text
                            fontStyle="normal"
                            fontWeight="900"
                            fontSize="11px"
                            color="#2F2F2F"
                          >
                            {hasAny ? entrance?.type : "-"}
                          </Text>
                          {hasAny && (
                            <Text
                              fontStyle="normal"
                              fontWeight="400"
                              fontSize="10px"
                              color="#6B6B6B"
                            >
                              usado / total
                            </Text>
                          )}
                        </Th>
                      );
                    })}
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {clients?.map((client) => (
                    <T.Body key={client?.id}>
                      <Td>
                        <Fonts.TableBodyText>
                          {client?.name}
                        </Fonts.TableBodyText>
                      </Td>
                      <Td textAlign="center">
                        <Fonts.TableBodyText>
                          {client.email}
                        </Fonts.TableBodyText>
                      </Td>
                      <Td textAlign="center">
                        <Fonts.TableBodyText>{client.cpf}</Fonts.TableBodyText>
                      </Td>
                      {client?.entrance?.map((entrance) => {
                        const total = entrance?.quantityInvitation ?? 0;
                        const isEmpty = total === 0;
                        const totalUsed = (client.guests ?? []).filter(
                          (guest: any) => guest.entranceId === entrance.id
                        ).length;
                        const isOver = totalUsed > total;
                        return (
                          <Td key={entrance?.id} textAlign="center">
                            <Text
                              fontStyle="normal"
                              fontWeight="400"
                              fontSize="14px"
                              color={isOver ? "red.500" : "#2F2F2F"}
                            >
                              {isEmpty ? "-" : `${totalUsed} / ${total}`}
                            </Text>
                          </Td>
                        );
                      })}
                      <Td>
                        <MenuActionButton
                          w="10%"
                          color="#2F2F2F"
                          onClick={() => setShowMenuPopUp(client?.id)}
                          _hover={{
                            bgColor: "neutral.200",
                          }}
                          _active={{
                            bgColor: "neutral.300",
                          }}
                        />
                      </Td>
                      {showMenuPopUp === client?.id && (
                        <MenuPopUp
                          top="-4px"
                          right="35px"
                          options={[
                            {
                              title: "Editar cliente",
                              onClick: () => handleEditClient(client),
                            },
                            {
                              title: "Excluir cliente",
                              onClick: () => handleDelete(client.id),
                            },
                          ]}
                          setShowMenuPopUp={setShowMenuPopUp}
                        />
                      )}
                    </T.Body>
                  ))}
                </Tbody>
                <Tfoot w="100%"></Tfoot>
              </Table>
              <Pagination page={page} lastPage={lastPage} onClick={setPage} />
            </TableContainer>
          </Stack>
        </Stack>
      </MainContainer>
    </>
  );
};
